import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DailyIframe from '@daily-co/daily-js';
import { apiService } from '../services/apiService';
import Stamp from './layout/Stamp';

// Module-level tracking for React Strict Mode
let dailyFrameInstance: ReturnType<typeof DailyIframe.createFrame> | null = null;

const formatElapsed = (secs: number): string => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

// Component
interface VideoRoomProps {
  sessionType?: 'mentor' | 'counselor';
  sessionId?: string;
}

const VideoRoom: React.FC<VideoRoomProps> = (props) => {
  const params = useParams<{ sessionType: string; sessionId: string }>();
  const navigate = useNavigate();

  const sessionType = (props.sessionType || params.sessionType) as 'mentor' | 'counselor';
  const sessionId = props.sessionId || params.sessionId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [recordingEnabled, setRecordingEnabled] = useState(false);

  // Recording consent state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [recordingDeclined, setRecordingDeclined] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [waitingForOther, setWaitingForOther] = useState(false);

  // Elapsed timer (starts once connected)
  const [elapsed, setElapsed] = useState(0);

  // Shared notes (mentor sessions)
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Ref to track if component is mounted
  const isMounted = useRef(true);
  const recordingStartedRef = useRef(false);

  // Join the video room
  const joinRoom = useCallback(async () => {
    console.log('[VideoRoom] joinRoom called, existing instance:', !!dailyFrameInstance);

    // If we already have an instance, reuse it
    if (dailyFrameInstance) {
      console.log('[VideoRoom] Reusing existing Daily instance');
      setLoading(false);
      return;
    }

    if (!sessionType || !sessionId) {
      setError('Invalid session parameters');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('[VideoRoom] Fetching room token...');
      const data = await apiService.post(`/video-rooms/join/${sessionType}/${sessionId}`);

      if (!data.roomUrl) {
        throw new Error(data.message || 'Failed to get room URL');
      }

      console.log('[VideoRoom] Got room URL:', data.roomUrl);

      // Check if component unmounted during API call
      if (!isMounted.current) {
        console.log('[VideoRoom] Component unmounted, aborting');
        return;
      }

      // Check again for existing instance (might have been created by concurrent call)
      if (dailyFrameInstance) {
        console.log('[VideoRoom] Instance created by concurrent call, reusing');
        setLoading(false);
        return;
      }

      setIsHost(data.isHost);
      setRecordingEnabled(data.recordingEnabled);

      // Create Daily.co iframe
      const container = document.getElementById('daily-container');
      if (!container) {
        throw new Error('Video container not found');
      }

      // Clear any existing iframes first
      container.innerHTML = '';

      console.log('[VideoRoom] Creating Daily iframe...');
      const frame = DailyIframe.createFrame(container, {
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: 'none',
        },
        showLeaveButton: false,
        showFullscreenButton: true,
      });

      dailyFrameInstance = frame;

      // Set up event listeners
      frame.on('joined-meeting', async () => {
        console.log('[VideoRoom] Successfully joined meeting');
        console.log('[VideoRoom] isHost:', data.isHost, 'recordingEnabled:', data.recordingEnabled);
        if (isMounted.current) {
          setLoading(false);
          // Show consent modal if recording is enabled
          if (data.recordingEnabled) {
            setShowConsentModal(true);
          }
        }
      });

      frame.on('participant-joined', (event) => {
        console.log('[VideoRoom] Participant joined:', event?.participant?.user_name);
        if (isMounted.current) {
          setParticipantCount(prev => prev + 1);
        }
      });

      frame.on('participant-left', (event) => {
        console.log('[VideoRoom] Participant left:', event?.participant?.user_name);
        if (isMounted.current) {
          setParticipantCount(prev => Math.max(0, prev - 1));
        }
      });

      frame.on('participant-counts-updated', (event) => {
        console.log('[VideoRoom] Participant count:', event?.participantCounts?.present);
        if (isMounted.current && event?.participantCounts?.present) {
          setParticipantCount(event.participantCounts.present);
        }
      });

      frame.on('recording-started', () => {
        console.log('[VideoRoom] Recording started event received');
        if (isMounted.current) {
          setIsRecording(true);
          setWaitingForOther(false);
        }
      });

      frame.on('recording-stopped', () => {
        console.log('[VideoRoom] Recording stopped event received');
        if (isMounted.current) {
          setIsRecording(false);
        }
      });

      frame.on('left-meeting', () => {
        console.log('[VideoRoom] Left meeting');
        dailyFrameInstance = null;
        navigate(-1);
      });

      frame.on('error', (event) => {
        console.error('[VideoRoom] Daily.co error:', event);
        if (isMounted.current) {
          setError('Video call error. Please try again.');
          setLoading(false);
        }
      });

      // Join the room
      console.log('[VideoRoom] Joining room...');
      await frame.join({
        url: data.roomUrl,
        token: data.token,
      });

      console.log('[VideoRoom] Join completed');
      if (isMounted.current) {
        setLoading(false);
      }
    } catch (err: any) {
      console.error('[VideoRoom] Failed to join video room:', err);
      if (isMounted.current) {
        setError(err.message || 'Failed to join video room');
        setLoading(false);
      }
      dailyFrameInstance = null;
    }
  }, [sessionType, sessionId, navigate]);

  // Handle recording consent
  const handleAcceptRecording = useCallback(async () => {
    console.log('[VideoRoom] User accepted recording');
    setConsentGiven(true);
    setShowConsentModal(false);

    // Only the host can start recording
    if (!isHost) {
      console.log('[VideoRoom] Not host, waiting for host to start recording');
      setWaitingForOther(true);
      return;
    }

    // Check if we should start recording (both parties present)
    if (participantCount >= 2 && dailyFrameInstance && !recordingStartedRef.current) {
      console.log('[VideoRoom] Both parties present, starting recording...');
      try {
        recordingStartedRef.current = true;
        // Call backend to start recording (more reliable)
        await apiService.post(`/video-rooms/recording/start/${sessionType}/${sessionId}`);
        // Also start via client SDK
        await dailyFrameInstance.startRecording();
        console.log('[VideoRoom] Recording started successfully');
      } catch (err) {
        console.error('[VideoRoom] Failed to start recording:', err);
        recordingStartedRef.current = false;
      }
    } else if (participantCount < 2) {
      console.log('[VideoRoom] Waiting for other participant to join');
      setWaitingForOther(true);
    }
  }, [participantCount, isHost, sessionType, sessionId]);

  const handleDeclineRecording = useCallback(() => {
    console.log('[VideoRoom] User declined recording');
    setRecordingDeclined(true);
    setShowConsentModal(false);
    setRecordingEnabled(false);
  }, []);

  // Auto-start recording when both parties join and consent is given (host only)
  useEffect(() => {
    if (
      isHost &&
      consentGiven &&
      participantCount >= 2 &&
      dailyFrameInstance &&
      !recordingStartedRef.current &&
      recordingEnabled &&
      !recordingDeclined
    ) {
      console.log('[VideoRoom] Conditions met, starting recording...');
      const startRecordingAsync = async () => {
        try {
          recordingStartedRef.current = true;
          setWaitingForOther(false);
          // Call backend to start recording (more reliable)
          await apiService.post(`/video-rooms/recording/start/${sessionType}/${sessionId}`);
          // Also start via client SDK
          if (dailyFrameInstance) {
            await dailyFrameInstance.startRecording();
          }
          console.log('[VideoRoom] Recording auto-started');
        } catch (err) {
          console.error('[VideoRoom] Failed to auto-start recording:', err);
          recordingStartedRef.current = false;
        }
      };
      startRecordingAsync();
    }
  }, [isHost, consentGiven, participantCount, recordingEnabled, recordingDeclined, sessionType, sessionId]);

  // Leave the call
  const leaveCall = useCallback(async () => {
    console.log('[VideoRoom] Leaving call...');

    // Store reference before any async operations
    const frame = dailyFrameInstance;
    dailyFrameInstance = null;

    try {
      // Notify backend that session is ending (triggers recording processing)
      await apiService.post(`/video-rooms/end-session/${sessionType}/${sessionId}`);
    } catch (err) {
      console.error('[VideoRoom] Failed to notify session end:', err);
    }

    if (frame) {
      try {
        // Stop recording before leaving (if it was started)
        try {
          await frame.stopRecording();
          console.log('[VideoRoom] Recording stopped');
        } catch (e) {
          // Recording might not have been started, ignore error
        }
        await frame.leave();
        frame.destroy();
      } catch (e) {
        console.error('[VideoRoom] Error leaving call:', e);
      }
    }
    navigate(-1);
  }, [navigate, sessionType, sessionId]);

  // Initialize on mount
  useEffect(() => {
    isMounted.current = true;
    joinRoom();

    return () => {
      console.log('[VideoRoom] Cleanup running...');
      isMounted.current = false;
      // Don't destroy on cleanup in Strict Mode - only on explicit leave
      // The dailyFrameInstance will be reused if component remounts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Elapsed timer once connected
  useEffect(() => {
    if (loading || error) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [loading, error]);

  const handleSaveNotes = async () => {
    if (!sessionId || !notes.trim()) return;
    setSavingNotes(true);
    const result = await apiService.updateMentorSessionNotes(sessionId, notes);
    setSavingNotes(false);
    if (result.success) {
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } else {
      alert(result.error || 'Failed to save notes');
    }
  };

  if (error) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap" style={{ maxWidth: 560 }}>
          <div className="card" style={{ padding: 32 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              Session room
            </span>
            <h1 style={{ fontSize: 24, marginBottom: 8 }}>Unable to join the video call</h1>
            <p className="meta" style={{ marginBottom: 20 }}>{error}</p>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      {/* Recording consent modal */}
      {showConsentModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(46, 45, 41, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div className="card" style={{ maxWidth: 460, padding: 32, background: 'var(--paper)' }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              Before you join
            </span>
            <h2 style={{ fontSize: 20, marginBottom: 10 }}>This session will be recorded</h2>
            <p className="meta" style={{ lineHeight: 1.6, marginBottom: 8 }}>
              The recording and a written summary are delivered to the family after the session —
              that's part of how Common Room keeps sessions accountable.
            </p>
            <p className="meta" style={{ lineHeight: 1.6, marginBottom: 20 }}>
              By continuing, you consent to being recorded. If you decline, the session goes ahead
              but no recording or summary will be available.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={handleAcceptRecording}>
                I agree — continue
              </button>
              <button className="btn btn-secondary" onClick={handleDeclineRecording}>
                Continue without recording
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="wrap">
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <h1 style={{ fontSize: 26 }}>
              {sessionType === 'mentor' ? 'Mentor session' : 'Counselor session'}
            </h1>
            <p className="meta" style={{ marginTop: 4 }}>
              Video session{!loading ? ` · ${formatElapsed(elapsed)} elapsed` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {isRecording && (
              <Stamp variant="verified">Recording on — both parties consented</Stamp>
            )}
            {recordingDeclined && <span className="tag">Recording off</span>}
            <button className="btn btn-secondary btn-sm" onClick={leaveCall}>
              Leave session
            </button>
          </div>
        </div>

        {/* Status notes */}
        {waitingForOther && !isRecording && isHost && (
          <div className="note" style={{ marginBottom: 16 }}>
            Waiting for the other participant to join before recording starts.
          </div>
        )}
        {waitingForOther && !isRecording && !isHost && consentGiven && (
          <div className="note" style={{ marginBottom: 16 }}>
            Recording will start when the host is ready.
          </div>
        )}
        {recordingDeclined && (
          <div className="note" style={{ marginBottom: 16 }}>
            Recording declined — no recording or written summary will be available for this
            session.
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1fr',
            gap: 24,
            alignItems: 'start',
          }}
        >
          {/* Video area */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                minHeight: 360,
                borderRadius: 12,
                overflow: 'hidden',
                border: '1px solid var(--hairline)',
                background: '#1f1e1b',
              }}
            >
              <div id="daily-container" style={{ position: 'absolute', inset: 0 }} />
              {loading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    color: 'var(--warm)',
                    background: '#1f1e1b',
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 500 }}>Connecting to the room…</div>
                  <div style={{ fontSize: 13, opacity: 0.7 }}>
                    Camera and microphone permissions may be requested.
                  </div>
                </div>
              )}
            </div>
            <p className="meta" style={{ marginTop: 10 }}>
              Use the in-call controls for mute, camera, screen share, and fullscreen.
            </p>
          </div>

          {/* Sidebar */}
          <aside style={{ minWidth: 0 }}>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 4 }}>Session template</h3>
              <p className="meta" style={{ marginBottom: 14 }}>
                Every mentor runs a screened template — no winging it.
              </p>
              <table className="data">
                <tbody>
                  <tr>
                    <td><span className="grid-chip admit">1</span></td>
                    <td>Context: school, goals, where you're stuck</td>
                    <td className="meta num-cell">10 min</td>
                  </tr>
                  <tr>
                    <td><span className="grid-chip admit">2</span></td>
                    <td>How the mentor navigated the same step</td>
                    <td className="meta num-cell">15 min</td>
                  </tr>
                  <tr>
                    <td><span className="grid-chip waitlist">3</span></td>
                    <td>Working session on your materials</td>
                    <td className="meta num-cell">25 min</td>
                  </tr>
                  <tr>
                    <td><span className="grid-chip reject">4</span></td>
                    <td>Written next steps, agreed together</td>
                    <td className="meta num-cell">10 min</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {sessionType === 'mentor' && (
              <div className="card warm" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, marginBottom: 8 }}>
                  Shared notes <span className="meta">· saved to the session</span>
                </h3>
                <textarea
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Key takeaways, drafts, agreed next steps…"
                  style={{
                    width: '100%',
                    border: '1px solid var(--hairline)',
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    fontFamily: 'inherit',
                    background: 'var(--paper)',
                    resize: 'vertical',
                    marginBottom: 10,
                  }}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={savingNotes || !notes.trim()}
                  onClick={handleSaveNotes}
                >
                  {savingNotes ? 'Saving…' : notesSaved ? 'Saved' : 'Save notes'}
                </button>
              </div>
            )}

            <div className="note">
              Ground rule: mentors never promise outcomes or quote "chances." They share their
              verified path — families draw their own conclusions.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default VideoRoom;
